import React from "react";
import { useNavigate } from "react-router-dom";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import moment from "moment";

import { assembleNotificationMessage } from "../../hooks/NotificationsHooks";
import { routes } from "../../../../constants";

const NotificationTableRow = ({
  notification,
  labelId,
  numberType,
  columnWidths,
  onQuickTaleClick,
  isNewNotification = false,
}) => {
  const message = assembleNotificationMessage(notification);
  
  // Handle different timestamp formats
  let formattedDate = "Invalid Date";
  try {
    if (notification.createdAt) {
      if (typeof notification.createdAt.toDate === 'function') {
        // Firestore Timestamp
        formattedDate = moment(notification.createdAt.toDate()).format("MM/DD/YYYY hh:mm A");
      } else if (notification.createdAt.seconds) {
        // Firestore Timestamp object with seconds
        formattedDate = moment(new Date(notification.createdAt.seconds * 1000)).format("MM/DD/YYYY hh:mm A");
      } else {
        // Regular Date or string
        formattedDate = moment(notification.createdAt).format("MM/DD/YYYY hh:mm A");
      }
    }
  } catch (error) {
    console.error("Error formatting date:", error);
    formattedDate = "Invalid Date";
  }

  const navigate = useNavigate();

  const handleTitleClick = (e) => {
    e.preventDefault();
    onQuickTaleClick(notification);
  };

  const handleReactionClick = (e) => {
    e.preventDefault();
    navigate(routes.reactionsInOrg);
  };

  const handleContributorClick = (e) => {
    e.preventDefault();
    navigate(routes.contributors); // Assuming 'routes.contributors' exists and points to '/contributors'
  };

  // Extract the title from the message for the clickable link
  const renderMessageWithClickableTitle = (message) => {
    // Handle contributor_request notifications
    if (notification.type === 'contributor_request') {
      const contributorText = "Contributor";
      const lastContributorIndex = message.lastIndexOf(contributorText);

      if (lastContributorIndex !== -1) {
        const beforeLink = message.substring(0, lastContributorIndex);
        const afterLink = message.substring(lastContributorIndex + contributorText.length);

        return (
          <>
            {beforeLink}
            <Link
              component="button"
              variant="body2"
              onClick={handleContributorClick}
              sx={{
                textDecoration: "underline",
                color: "primary.main",
                cursor: "pointer",
                fontWeight: "inherit",
                fontSize: "inherit",
                fontFamily: "inherit",
              }}
            >
              {contributorText}
            </Link>
            {afterLink}
          </>
        );
      }
      return message; // If "Contributor" is not found, return the original message
    }

    // Handle quicktale_reaction notifications for the second "Audio Reaction"
    if (notification.type === 'quicktale_reaction') {
      const audioReactionText = "Audio Reaction";
      // Find the first occurrence
      const firstIndex = message.indexOf(audioReactionText);
      if (firstIndex !== -1) {
        // Find the second occurrence after the first one
        const secondIndex = message.indexOf(audioReactionText, firstIndex + audioReactionText.length);

        if (secondIndex !== -1) {
          const beforeLink = message.substring(0, secondIndex);
          const afterLink = message.substring(secondIndex + audioReactionText.length);

          return (
            <>
              {beforeLink}
              <Link
                component="button"
                variant="body2"
                onClick={handleReactionClick}
                sx={{
                  textDecoration: "underline",
                  color: "primary.main",
                  cursor: "pointer",
                  fontWeight: "inherit",
                  fontSize: "inherit",
                  fontFamily: "inherit",
                }}
              >
                {audioReactionText}
              </Link>
              {afterLink}
            </>
          );
        }
      }
      return message; // If the second "Audio Reaction" is not found, return the original message
    }

    // Existing logic for quicktale_submission and quicktale_reaction
    if (!notification.quicktaleData?.title) {
      return message;
    }

    const title = notification.quicktaleData.title;
    const titleIndex = message.indexOf(title);
    
    if (titleIndex === -1) {
      return message;
    }

    // For reaction notifications, also make "reaction" clickable
    if (notification.type === "quicktale_reaction") {
      const reactionIndex = message.indexOf("reaction");
      
      if (reactionIndex !== -1 && reactionIndex < titleIndex) {
        // "reaction" comes before the title
        const beforeReaction = message.substring(0, reactionIndex);
        const betweenReactionAndTitle = message.substring(reactionIndex + "reaction".length, titleIndex);
        const afterTitle = message.substring(titleIndex + title.length);

        return (
          <>
            {beforeReaction}
            <Link
              component="button"
              variant="body2"
              onClick={handleReactionClick}
              sx={{
                textDecoration: "underline",
                color: "primary.main",
                cursor: "pointer",
                fontWeight: "inherit",
                fontSize: "inherit",
                fontFamily: "inherit",
              }}
            >
              reaction
            </Link>
            {betweenReactionAndTitle}
            <Link
              component="button"
              variant="body2"
              onClick={handleTitleClick}
              sx={{
                textDecoration: "underline",
                color: "primary.main",
                cursor: "pointer",
                fontWeight: "inherit",
                fontSize: "inherit",
                fontFamily: "inherit",
              }}
            >
              {title}
            </Link>
            {afterTitle}
          </>
        );
      }
    }

    // Default behavior for non-reaction notifications or when reaction word is not found
    const beforeTitle = message.substring(0, titleIndex);
    const afterTitle = message.substring(titleIndex + title.length);

    return (
      <>
        {beforeTitle}
        <Link
          component="button"
          variant="body2"
          onClick={handleTitleClick}
          sx={{
            textDecoration: "underline",
            color: "primary.main",
            cursor: "pointer",
            fontWeight: "inherit",
            fontSize: "inherit",
            fontFamily: "inherit",
          }}
        >
          {title}
        </Link>
        {afterTitle}
      </>
    );
  };

  return (
    <TableRow
      hover
      role="checkbox"
      tabIndex={-1}
      key={notification.id}
      sx={{
        cursor: "pointer",
        backgroundColor: isNewNotification 
          ? "#e3f2fd" // Pale blue for new notifications
          : numberType ? "#fafafa" : "#fff",
        "&:hover": {
          backgroundColor: isNewNotification 
            ? "#bbdefb" // Slightly darker blue on hover for new notifications
            : "#f5f5f5",
        },
      }}
    >
      <TableCell
        component="th"
        id={labelId}
        scope="row"
        sx={{
          width: columnWidths?.message || 600,
          maxWidth: columnWidths?.message || 600,
          padding: "16px",
        }}
      >
        <Typography variant="body2" sx={{ wordWrap: "break-word" }}>
          {renderMessageWithClickableTitle(message)}
        </Typography>
      </TableCell>
      
      <TableCell
        align="left"
        sx={{
          width: columnWidths?.createdAt || 200,
          maxWidth: columnWidths?.createdAt || 200,
          padding: "16px",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {formattedDate}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

export default NotificationTableRow;
